/**
 * Error Badge component for the application header.
 * Shows an alert icon with a badge count for unread errors.
 *
 * @module components/ui/ErrorBadge
 */

import { AlertTriangle } from 'lucide-react';
import { clsx } from 'clsx';
import { useErrorStore } from '@/stores/useErrorStore';

export interface ErrorBadgeProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * ErrorBadge - Header button showing error count
 *
 * Features:
 * - Only visible when there are errors
 * - Shows alert icon with red badge
 * - Clicking opens the ErrorDrawer
 *
 * @example
 * <ErrorBadge />
 */
export function ErrorBadge({ className }: ErrorBadgeProps) {
  const { unreadCount, errors, openDrawer } = useErrorStore();

  // Don't render if there are no errors
  if (errors.length === 0) {
    return null;
  }

  // Show total count if all are read, otherwise show unread count
  const displayCount = unreadCount > 0 ? unreadCount : errors.length;

  return (
    <button
      type="button"
      onClick={openDrawer}
      className={clsx(
        'relative rounded-lg p-2 text-gray-500 dark:text-gray-400',
        'hover:text-gray-700 dark:hover:text-gray-200',
        'hover:bg-gray-100 dark:hover:bg-[#2a2a2a]',
        'transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-[#0a0a0a]',
        className
      )}
      title={`${errors.length} error${errors.length === 1 ? '' : 's'}`}
      aria-label={`View ${errors.length} error${errors.length === 1 ? '' : 's'}`}
    >
      <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400" />

      {/* Badge */}
      <span
        className={clsx(
          'absolute -top-0.5 -right-0.5 flex items-center justify-center',
          'min-w-[18px] h-[18px] px-1',
          'text-[10px] font-bold text-white',
          'rounded-full',
          unreadCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-gray-400 dark:bg-gray-600'
        )}
      >
        {displayCount > 99 ? '99+' : displayCount}
      </span>
    </button>
  );
}
